/**
 * App Tour catalogue — read-side helpers over the generated lesson list.
 * Data comes from docs/tour/lessons.md via scripts/gen-tour-catalog.mjs.
 *
 * Ported from Sun Stellar (2026-10-02). Sun Stellar sorts people into sales
 * profiles; Veda sorts them by department, so who-sees-what follows the
 * lesson list's "Who" column (lessons.md, "Who decides who sees the lesson").
 */
import {
  TOUR_EVERYONE_WEB_FIRST_WEEK,
  TOUR_FIRST_DAY,
  TOUR_LESSONS,
  TOUR_MANAGER_PATHS,
  TOUR_MODULES,
} from './tourCatalog.generated';
import type { TourLesson, TourLessonAudience, TourManagerPathKey, TourModule, TourPathKey } from './tourTypes';

export type { TourLesson, TourModule } from './tourTypes';
export type TourPlatform = 'web' | 'android';
export type TourScope = 'first-day' | 'manager' | 'all';

/**
 * Who is looking, from the role name:
 * - admin            admin / owner / super_admin — every lesson
 * - salesManager     sales_manager — sales + "Manager only"
 * - sales            sales_rep — sales
 * - purchaseHead     purchase_head — purchase (not the PM-menu pages)
 * - purchaseManager  purchase / purchase_manager — purchase + "Purchase (PM)"
 * - logistics        logistics_manager / logistics_head / LOGISTICS
 * - accountant       accountant
 * - other            anyone else — only the "Everyone" lessons
 */
export type TourRole = 'admin' | 'salesManager' | 'sales' | 'purchaseHead' | 'purchaseManager' | 'logistics' | 'accountant' | 'other';

const ROLE_BY_NAME: Record<string, TourRole> = {
  admin: 'admin',
  owner: 'admin',
  super_admin: 'admin',
  sales_manager: 'salesManager',
  sales_rep: 'sales',
  purchase_head: 'purchaseHead',
  purchase: 'purchaseManager',
  purchase_manager: 'purchaseManager',
  logistics_manager: 'logistics',
  logistics_head: 'logistics',
  logistics: 'logistics',
  accountant: 'accountant',
};

export function tourRoleFor(roleName: string | null | undefined): TourRole {
  return ROLE_BY_NAME[(roleName ?? '').trim().toLowerCase()] ?? 'other';
}

/**
 * The admin's "View sidebar as another role" switch (TopBar → AppShell keeps it in
 * sessionStorage 'aura-view-override'): the tour follows the view being previewed.
 */
const ROLE_BY_VIEW: Record<string, TourRole> = {
  owner: 'admin',
  super_admin: 'admin',
  veda_sales_executive: 'sales',
  sales_executive: 'sales',
  veda_sales_manager: 'salesManager',
  sales: 'salesManager',
  veda_purchase_head: 'purchaseHead',
  purchase: 'purchaseManager',
  veda_logistics_manager: 'logistics',
  accountant: 'accountant',
};

export function tourRoleForView(view: string | null | undefined): TourRole | null {
  if (!view) return null;
  return ROLE_BY_VIEW[view] ?? 'other';
}

/** Which lesson audiences each role sees. Admin sees everything. */
const SEES: Record<TourRole, readonly TourLessonAudience[]> = {
  admin: ['sales', 'salesManager', 'purchase', 'purchasePM', 'logistics', 'accountant', 'admin', 'everyone'],
  salesManager: ['sales', 'salesManager', 'everyone'],
  sales: ['sales', 'everyone'],
  purchaseHead: ['purchase', 'everyone'],
  purchaseManager: ['purchase', 'purchasePM', 'everyone'],
  logistics: ['logistics', 'everyone'],
  accountant: ['accountant', 'everyone'],
  other: ['everyone'],
};

const PATH_KEY: Record<TourRole, TourPathKey | null> = {
  admin: 'admin',
  salesManager: 'sales',
  sales: 'sales',
  purchaseHead: 'purchase',
  purchaseManager: 'purchase',
  logistics: 'logistics',
  accountant: 'accountant',
  other: null,
};

const MANAGER_PATH_KEY: Partial<Record<TourRole, TourManagerPathKey>> = {
  salesManager: 'salesManager',
  purchaseHead: 'purchaseHead',
};

const SUBTITLE: Record<TourRole, string> = {
  admin: 'Short lessons for every department.',
  salesManager: 'Short lessons for the sales team.',
  sales: 'Short lessons for the sales team.',
  purchaseHead: 'Short lessons for the purchase team.',
  purchaseManager: 'Short lessons for the purchase team.',
  logistics: 'Short lessons for the logistics team.',
  accountant: 'Short lessons for accounts.',
  other: 'Short lessons for everyone.',
};

const BY_ID = new Map(TOUR_LESSONS.map((lesson) => [lesson.id, lesson]));

export function tourSubtitle(role: TourRole): string {
  return SUBTITLE[role];
}

export function findLesson(id: string | undefined): TourLesson | undefined {
  return id ? BY_ID.get(id) : undefined;
}

export function findModule(id: string | undefined): TourModule | undefined {
  return TOUR_MODULES.find((module) => module.id === id);
}

/** Role filter: the lesson's audience must be one this role sees. */
export function canSeeLesson(lesson: TourLesson, role: TourRole): boolean {
  return SEES[role].includes(lesson.audience);
}

/** Web lists Web + Both lessons; the Android switch lists Android + Both. */
export function onPlatform(lesson: TourLesson, platform: TourPlatform): boolean {
  return lesson.platform === 'Both' || lesson.platform === (platform === 'web' ? 'Web' : 'Android');
}

/** True when this role has any phone lesson (sales, the admin) — only then is there a platform switch. */
export function hasAndroidLessons(role: TourRole): boolean {
  return TOUR_LESSONS.some((lesson) => canSeeLesson(lesson, role) && onPlatform(lesson, 'android'));
}

/** True when the role has a manager path (sales manager, purchase head). */
export function hasManagerPath(role: TourRole): boolean {
  return Boolean(MANAGER_PATH_KEY[role]);
}

/**
 * "Allowed": the lesson list says the lesson may run as a walk. Android lessons are
 * watch-only on the web. Whether a walk is actually wired is `hasWalk` in tourWalks.ts.
 */
export function canShowMe(lesson: TourLesson, platform: TourPlatform): boolean {
  return platform === 'web' && lesson.showMe.allowed;
}

export function lessonMinutes(lesson: TourLesson, platform: TourPlatform): number {
  return lesson.minutes[platform] ?? lesson.minutes.web ?? lesson.minutes.android ?? 0;
}

export function matchesSearch(lesson: TourLesson, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const module = findModule(lesson.moduleId);
  return `${lesson.id} ${lesson.title} ${module?.title ?? ''}`.toLowerCase().includes(q);
}

export interface TourFilter {
  role: TourRole;
  platform: TourPlatform;
  scope: TourScope;
}

function pathIds(role: TourRole, platform: TourPlatform, scope: TourScope): string[] {
  if (scope === 'manager') {
    const key = MANAGER_PATH_KEY[role];
    return key ? TOUR_MANAGER_PATHS[key] : [];
  }
  const key = PATH_KEY[role];
  const ids = [...(key ? TOUR_FIRST_DAY[key] : []), ...(platform === 'web' ? TOUR_EVERYONE_WEB_FIRST_WEEK : [])];
  return [...new Set(ids)];
}

/**
 * Every lesson this person can open on this platform. "All modules" keeps
 * catalogue order; a path keeps the order written in the lesson list.
 */
export function visibleLessons({ role, platform, scope }: TourFilter): TourLesson[] {
  const fits = (lesson: TourLesson) => canSeeLesson(lesson, role) && onPlatform(lesson, platform);
  if (scope === 'all') return TOUR_LESSONS.filter(fits);
  return pathIds(role, platform, scope).map((id) => BY_ID.get(id)).filter((l): l is TourLesson => !!l && fits(l));
}

export function visibleModules(lessons: TourLesson[]): TourModule[] {
  const ids = new Set(lessons.map((lesson) => lesson.moduleId));
  return TOUR_MODULES.filter((module) => ids.has(module.id));
}
